import { css } from '@emotion/css';
import { useId, useMemo, useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import Skeleton from 'react-loading-skeleton';

import { dateTime, GrafanaTheme2 } from '@grafana/data';
import { useTranslate, Trans } from '@grafana/i18n';
import {
  Avatar,
  Box,
  Button,
  Field,
  IconButton,
  InlineSwitch,
  Input,
  Stack,
  TagsInput,
  Text,
  useStyles2,
} from '@grafana/ui';
import { attachSkeleton, SkeletonComponent } from '@grafana/ui/unstable';
import icnDatasourceSvg from 'img/icn-datasource.svg';

import { useUpdateQueryTemplateMutation } from '../../api/clients/querylibrary';
import {
  queryLibraryTrackAddOrEditDescription,
  queryLibraryTrackAddOrEditTitle,
  queryLibraryTrackEditIsVisible,
} from '../QueryLibraryAnalyticsEvents';
import { QueryTemplateRow } from '../types';
import { EditQueryTemplateCommand } from '../utils/types';
import { useDatasource } from '../utils/useDatasource';

export type QueryDetails = {
  title: string;
  description: string;
  isVisible: boolean;
  tags: string[];
};

export interface QueryDetailsProps {
  query: QueryTemplateRow;
}
function QueryLibraryDetailsComponent({ query }: QueryDetailsProps) {
  const { t } = useTranslate();
  const [editingQuery, setEditingQuery] = useState(false);
  const datasourceApi = useDatasource(query.datasourceRef);
  const formattedTime = dateTime(query.createdAtTimestamp).toLocaleString();
  const { isLocked } = query;

  const AUTHOR_ID = useId();
  const DATASOURCE_ID = useId();
  const DESCRIPTION_ID = useId();
  const DATE_ADDED_ID = useId();
  const IS_VISIBLE_ID = useId();
  const TITLE_INPUT_ID = useId();
  const TAGS_ID = 'tags';
  const styles = useStyles2(getStyles);

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { dirtyFields, isDirty },
  } = useForm<QueryDetails>({
    defaultValues: {
      title: query.title,
      description: query.description,
      isVisible: query.isVisible,
    },
  });

  // Resets React Form Hook with new default values when selecting new query in library
  useEffect(() => {
    reset({
      title: query.title,
      description: query.description,
      isVisible: query.isVisible,
      tags: query.tags ?? [],
    });
  }, [query, reset]);

  const [editQueryTemplate] = useUpdateQueryTemplateMutation();

  const handleEditQueryTemplate = (editQueryTemplateCommand: EditQueryTemplateCommand) => {
    return editQueryTemplate({
      name: editQueryTemplateCommand.uid,
      patch: {
        spec: editQueryTemplateCommand.partialSpec,
      },
    });
  };

  const onEditCancel = () => {
    setEditingQuery(false);
  };

  const onSubmit = async (data: QueryDetails) => {
    onEditCancel();
    fireAnalyticEvents();
    if (query?.uid) {
      handleEditQueryTemplate({ uid: query.uid, partialSpec: { ...data } });
    }
  };

  const fireAnalyticEvents = () => {
    if (dirtyFields.title) {
      queryLibraryTrackAddOrEditTitle();
    }
    if (dirtyFields.description) {
      queryLibraryTrackAddOrEditDescription();
    }
    if (dirtyFields.isVisible) {
      queryLibraryTrackEditIsVisible();
    }
  };

  const editButton = useMemo(() => {
    if (!editingQuery) {
      return (
        <IconButton
          onClick={() => {
            setEditingQuery(true);
          }}
          name="pen"
          disabled={isLocked}
          tooltip={t('query-library.query-details.edit-button', 'Edit query')}
        />
      );
    }
    if (isDirty) {
      return (
        <Button variant="primary" type="submit">
          <Trans i18nKey="explore.query-library.save">Save</Trans>
        </Button>
      );
    }
    return (
      <Button variant="secondary" onClick={onEditCancel}>
        <Trans i18nKey="explore.query-library.cancel">Cancel</Trans>
      </Button>
    );
  }, [editingQuery, isDirty, t, isLocked]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box minWidth={0}>
        <Box
          display="flex"
          gap={1}
          alignItems="center"
          justifyContent="space-between"
          marginBottom={editingQuery ? 0 : 2}
        >
          <Stack gap={1} alignItems="center" minWidth={0}>
            <img
              className={styles.logo}
              src={datasourceApi?.meta.info.logos.small || icnDatasourceSvg}
              alt={datasourceApi?.type}
            />
            {editingQuery ? (
              <Field label={t('query-library.query-details.title', 'Title')}>
                <Input data-testid="query-title-input" id={TITLE_INPUT_ID} {...register('title')} />
              </Field>
            ) : (
              <Text variant="h5" truncate>
                {query.title ?? ''}
              </Text>
            )}
          </Stack>
          {editButton}
        </Box>
        <code className={styles.query}>{query.queryText}</code>
        <Field label={t('query-library.query-details.datasource', 'Data source')}>
          <Input readOnly id={DATASOURCE_ID} value={query.datasourceName} />
        </Field>
        <Field label={t('query-library.query-details.description', 'Description')}>
          <Input id={DESCRIPTION_ID} readOnly={!editingQuery} {...register('description')} />
        </Field>
        <Field label={t('query-library.query-details.tags', 'Tags')} htmlFor={TAGS_ID}>
          <Controller
            name={TAGS_ID}
            control={control}
            defaultValue={query.tags ?? []}
            render={({ field: { ref, value, onChange, ...field } }) => {
              return (
                <TagsInput
                  {...field}
                  id={TAGS_ID}
                  disabled={!editingQuery}
                  autoColors={false}
                  onChange={(tags) => {
                    onChange(Array.from(new Set(tags)).sort());
                  }}
                  tags={value ? Array.from(value) : []}
                />
              );
            }}
          />
        </Field>
        <Field label={t('query-library.query-details.author', 'Author')}>
          <Input
            readOnly
            id={AUTHOR_ID}
            prefix={
              <Box marginRight={0.5}>
                <Avatar
                  width={2}
                  height={2}
                  src={query.user?.avatarUrl || 'https://secure.gravatar.com/avatar'}
                  alt=""
                />
              </Box>
            }
            value={query.user?.displayName}
          />
        </Field>
        <Field label={t('query-library.query-details.date-added', 'Date added')}>
          <Input readOnly id={DATE_ADDED_ID} value={formattedTime} />
        </Field>
        <InlineSwitch
          showLabel
          label={t('query-library.query-details.make-query-visible', 'Make query visible')}
          id={IS_VISIBLE_ID}
          disabled={!editingQuery}
          {...register('isVisible')}
        />
      </Box>
    </form>
  );
}

const QueryLibraryDetailsSkeleton: SkeletonComponent = ({ rootProps }) => {
  const skeletonStyles = useStyles2(getSkeletonStyles);
  return (
    <Box minWidth={0} {...rootProps}>
      <Stack direction="column" gap={2}>
        <Stack gap={1} alignItems="center" minWidth={0}>
          <Skeleton circle width={24} height={24} containerClassName={skeletonStyles.icon} />
          <Skeleton width={120} />
        </Stack>

        <Skeleton height={32} />

        <Box>
          <Skeleton width={60} />
          <Skeleton height={32} />
        </Box>

        <Box>
          <Skeleton width={60} />
          <Skeleton height={32} />
        </Box>

        <Box>
          <Skeleton width={60} />
          <Skeleton height={32} />
        </Box>
      </Stack>
    </Box>
  );
};

export const QueryLibraryDetails = attachSkeleton(QueryLibraryDetailsComponent, QueryLibraryDetailsSkeleton);

const getSkeletonStyles = () => ({
  icon: css({
    display: 'block',
    lineHeight: 1,
  }),
});

const getStyles = (theme: GrafanaTheme2) => ({
  query: css({
    backgroundColor: theme.colors.action.disabledBackground,
    borderRadius: theme.shape.radius.default,
    display: 'block',
    margin: theme.spacing(0, 0, 2, 0),
    overflowWrap: 'break-word',
    padding: theme.spacing(1),
    whiteSpace: 'pre-wrap',
  }),
  logo: css({
    width: '24px',
  }),
});
